"use client";

import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FrequentlyAskedQuestions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const faqs = [{
    question: "How long does a typical website design project take?",
    answer: "Most of our custom website projects are completed within 4 to 6 weeks. We prioritize clear communication and efficient workflows to ensure your business gets online quickly without sacrificing quality or performance."
  }, {
    question: "Do you provide ongoing SEO maintenance?",
    answer: "Yes, we offer monthly SEO packages to keep your site ranking high. SEO is an evolving process, and our team continuously monitors algorithm updates and competitor activity to maintain your competitive edge."
  }, {
    question: "Can you fix my Google Business Profile issues?",
    answer: "Absolutely. We specialize in resolving verification errors, duplicate listings, and ranking drops. We ensure your local presence is accurate, optimized, and visible to customers searching in your area."
  }, {
    question: "Do you work with small businesses specifically?",
    answer: "Small to medium-sized businesses are our primary focus. We understand the unique challenges you face and provide tailored, cost-effective digital solutions that drive real growth and measurable results."
  }, {
    question: "What payment structures do you offer?",
    answer: "We offer flexible payment plans for our services. For larger projects, we typically break costs into milestones, and we accept all major credit cards, bank transfers, and digital payment methods."
  }, {
    question: "How do I know if my website needs an update?",
    answer: "If your site is not mobile-friendly, takes more than 3 seconds to load, or isn't generating leads, it's time for an upgrade. We provide free audits to identify exactly where your site is underperforming."
  }, {
    question: "Are your SEO strategies ethical?",
    answer: "We strictly follow 'White Hat' SEO practices. We focus on high-quality content, technical optimization, and genuine authority building to ensure long-term success that complies with all search engine guidelines."
  }, {
    question: "Do you provide reporting on performance?",
    answer: "Transparency is key. We provide monthly performance reports that detail your traffic, keyword rankings, and lead generation metrics so you can see the direct impact of our work on your business."
  }];
  return <section data-section-id="850" id="frequently-asked-questions" className="bg-background py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={ref} className={`grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <p className="font-default text-xs uppercase tracking-[0.2em] text-primary mb-6">
                [ FAQ ]
              </p>
              <h2 className="font-default text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-foreground mb-6">
                Answers to your{" "}
                <em className="font-default italic text-primary">
                  digital growth questions.
                </em>
              </h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8 max-w-md">
                Clear insights into our design process, SEO strategies, and how we help local businesses dominate their market.
              </p>
              <Link to="/ContactUsPage" className="inline-flex items-center gap-2 font-default text-xs uppercase tracking-[0.15em] text-foreground border-b border-foreground/30 pb-1 hover:text-primary hover:border-primary transition-colors duration-300">
                Still have questions? Contact us
                <ArrowRight className="size-3.5" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full border-t border-border">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border group">
                  <AccordionTrigger className="py-8 hover:no-underline [&>svg]:hidden [&[data-state=open]_.plus-icon]:rotate-45 [&[data-state=open]_.question-text]:text-primary">
                    <span className="question-text font-default text-2xl md:text-[1.75rem] leading-tight text-foreground text-left pr-4 transition-colors duration-300 group-hover:text-primary">
                      {faq.question}
                    </span>
                    <span className="plus-icon shrink-0 mt-2 text-secondary text-2xl font-light leading-none transition-transform duration-300 ease-out">
                      +
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pr-12">
                    <p className="text-base text-muted-foreground leading-[1.7]">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}